import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { bs } from '../../../../styles/styles';
import { Button } from '../../../Button';
import { CustomDateRange } from '../../../CustomDateRange';
import { Dropdown } from '../../../Dropdown';
import { DisclosureIcon } from '../../../svgs/icons/DisclosureIcon';
import { styleSheet as dropdownStyles } from '../../DropdownComponent/styles';
import { DateRangeOption, IDayOption } from '../../models';
import { dayOptions } from '../../utils';
import { styleSheet } from './styles';

export const ServiceJobDateFilterDropdown = ({
	selectedMinimumDate,
	setSelectedMinimumDate,
	selectedMaximumDate,
	setSelectedMaximumDate,
	anchorClassName,
}: {
	selectedMinimumDate: string | null;
	setSelectedMinimumDate: React.Dispatch<React.SetStateAction<string | null>>;
	selectedMaximumDate: string | null;
	setSelectedMaximumDate: React.Dispatch<React.SetStateAction<string | null>>;
	anchorClassName?: string;
}) => {
	const [isOpen, setIsOpen] = React.useState(false);
	// Tracks the user having picked "Custom" before any dates are chosen — the only piece of intent
	// not yet reflected in the selected dates. Everything else is derived from the date props.
	const [isCustomPending, setIsCustomPending] = React.useState(false);

	const selectedDayOption = React.useMemo<IDayOption | null>(() => {
		if (selectedMinimumDate && selectedMaximumDate) {
			return dayOptions.find(option => option.value === DateRangeOption.Custom) ?? null;
		}
		return dayOptions.find((option: IDayOption) => option.value === selectedMinimumDate) ?? null;
	}, [selectedMinimumDate, selectedMaximumDate]);

	const isCustomSelected = isCustomPending || selectedDayOption?.value === DateRangeOption.Custom;

	const getDateRangeMessage = () => {
		if (!selectedMinimumDate || !selectedDayOption) {
			return 'Completed Date';
		}

		if (selectedDayOption.value === DateRangeOption.Custom && selectedMinimumDate && selectedMaximumDate) {
			const selectedMinimumDateString = moment(selectedMinimumDate).format('MM/DD');
			const selectedMaximumDateString = moment(selectedMaximumDate).format('MM/DD');
			return `Completed ${selectedMinimumDateString} - ${selectedMaximumDateString}`;
		}
		const key = selectedDayOption.key;
		return `Completed / ${DateRangeOption[key]}`;
	};

	const dropdownMessage = getDateRangeMessage();

	const filteredDayOptions = dayOptions.filter(option => {
		if (option.value === DateRangeOption.Custom && selectedDayOption?.value === DateRangeOption.Custom) {
			return false;
		}
		return true;
	});

	const selectDateRangeOption = (newDayOption: IDayOption, ev: React.MouseEvent<HTMLButtonElement>) => {
		if (newDayOption.value === DateRangeOption.Custom) {
			setIsCustomPending(true);
			ev.stopPropagation();
			ev.preventDefault();
		} else {
			setIsCustomPending(false);
			setSelectedMinimumDate(newDayOption.value);
			setIsOpen(false);
		}
	};

	const resetFilter = () => {
		setIsCustomPending(false);
		setSelectedMinimumDate(null);
		setSelectedMaximumDate(null);
	};

	return (
		<Dropdown
			anchor={
				<div className={css(dropdownStyles.dropdownAnchor)}>
					<div className={css(bs.truncateText)}>{dropdownMessage}</div>
					<DisclosureIcon className={css(dropdownStyles.dropdownIcon)} />
				</div>
			}
			anchorClassName={anchorClassName}
			disabled={false}
			className={css(dropdownStyles.dropdownInline, isOpen ? dropdownStyles.dropdownInlineOpen : null)}
			contentClassName={css(dropdownStyles.dropdownMenu, styleSheet.dropdownMenuOverrides)}
			contentPositionY='bottom'
			contentInnerClassName={css(styleSheet.dropdownContentInnerOverrides)}
			isOpen={isOpen}
			onOpenChanged={() => setIsOpen(!isOpen)}
			openOnClick={true}
		>
			<div className={css(dropdownStyles.dropdown)}>
				<div>
					{isCustomSelected ? (
						<CustomDateRange
							endDateRequired
							resetDates={() => {
								setSelectedMinimumDate(null);
								setSelectedMaximumDate(null);
							}}
							defaultOpen={!selectedMaximumDate}
							from={selectedMinimumDate ? moment(selectedMinimumDate).toDate() : new Date()}
							to={selectedMaximumDate ? moment(selectedMaximumDate).toDate() : null}
							onChange={(from, to) => {
								setSelectedMinimumDate(moment(from).format('YYYY-MM-DD'));
								setSelectedMaximumDate(to ? moment(to).format('YYYY-MM-DD') : moment(from).format('YYYY-MM-DD'));
							}}
							showTextInput
							disableScrollTo
						/>
					) : null}
				</div>

				<div>
					{!isCustomSelected &&
						filteredDayOptions.map(dayOption => {
							return (
								<Button
									label={dayOption.label}
									key={`service-job-date-range-option-${dayOption.key}`}
									onClick={(e: React.MouseEvent<HTMLButtonElement>) => selectDateRangeOption(dayOption, e)}
									kind='custom'
									className={css(
										dropdownStyles.option,
										selectedDayOption?.key === dayOption.key ? dropdownStyles.selected : null
									)}
								/>
							);
						})}
				</div>

				<Button label='Reset Filter' kind='custom' className={css(bs.brandLink)} type='button' onClick={resetFilter} />
			</div>
		</Dropdown>
	);
};
